import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
    FormControl
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as moment from 'moment';

import { BookingService } from '@app/_services/booking.service';
import { Product } from '@app/_interfaces/product';
import { TimeSlotSelectionData } from '@app/_interfaces/time-slot-selection.interface';
import { CompaniesService } from '@app/_services';
import { QuotationRequestService } from '@app/_services/quotation-request.service';
import { AuthenticationService } from '@app/_services';
import { Router } from '@angular/router';
import { InputNumberNewComponent } from '@app/shared/components/input-number-new/input-number-new.component';
import { CartService } from '@services/cart.service';
import { FormDataHelper } from '@app/_helpers';
import { CurrencyConvertPipe } from '@app/shared/pipes';
import * as _ from 'lodash';

// Interface for time slot configuration
interface TimeSlotConfig {
    startTime: string; // Format: "HH:mm"
    endTime: string; // Format: "HH:mm"
    duration: number; // Duration in hours
    price: number; // Price per slot
    isRecurring: boolean; // Whether this slot repeats daily
    daysOfWeek?: number[]; // Days when this slot is available (0=Sunday, 6=Saturday)
}

// Interface for available time slot with booking status
interface AvailableTimeSlot {
    id: string;
    startTime: string;
    endTime: string;
    duration: number; // Duration in hours
    price: number;
    totalQuantity: number;
    bookedQuantity: number;
    availableQuantity: number;
    isAvailable: boolean;
    isSelected: boolean;
}

// Interface for selected time slot booking
interface SelectedTimeSlot {
    slotId: string;
    startTime: string;
    endTime: string;
    price: number;
    quantity: number; // Add quantity property
}

// Interface for calendar day
interface CalendarDay {
    date: string;
    day: number;
    isCurrentMonth: boolean;
    isToday: boolean;
    isAvailable: boolean;
    isSelected: boolean;
    isUnavailable: boolean;
    hasSlots: boolean;
}

// Interface for calendar month
interface CalendarMonth {
    year: number;
    month: number;
    monthName: string;
    days: CalendarDay[];
}

// Interface for API response
interface TimeSlotApiResponse {
    code: number;
    msg?: string;
    selectedDate?: string;
    availableSlots?: any[];
    bookingConfig?: {
        advanceBookingHours: number;
        holdTimeMinutes: number;
        maxSlotsPerBooking: number;
        requireApproval: boolean;
        productQuantity: number;
    };
    booking?: any;
}

@Component({
    selector: 'app-time-slot-booking',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TranslateModule,
        InputNumberNewComponent,
        CurrencyConvertPipe
    ],
    templateUrl: './time-slot-booking.component.html',
    styleUrls: ['./time-slot-booking.component.scss']
})
export class TimeSlotBookingComponent implements OnInit, OnDestroy {
    // === INPUT/OUTPUT PROPERTIES ===

    /** Product being booked - contains all product and booking configuration */
    @Input() product: Product;

    /** Emits when a booking is successfully created */
    @Output() bookingCreated = new EventEmitter<any>();

    /** Emits when an item is added to cart */
    @Output() addedToCart = new EventEmitter<any>();

    /** Emits when the total price changes */
    @Output() priceCalculated = new EventEmitter<number>();

    /** Emits when selected slots change */
    @Output() selectedSlotsChanged = new EventEmitter<TimeSlotSelectionData>();

    /** Emits when a quotation request is successfully created */
    @Output() quotationRequested = new EventEmitter<any>();

    // === COMPONENT STATE ===

    private destroy$ = new Subject<void>();

    // Form management
    bookingForm: FormGroup;
    quotationForm: FormGroup;
    loading = false;
    showQuotationForm = false;

    // Date selection
    selectedDate: string = '';
    calendarMonths: CalendarMonth[] = [];
    currentMonthIndex = 0;

    // Time slot management
    availableSlots: AvailableTimeSlot[] = [];
    selectedSlots: SelectedTimeSlot[] = [];
    slotConfig: TimeSlotConfig[] = [];

    // Pricing and calculations
    totalPrice = 0;
    totalSlots = 0;

    /** Form control for quantity selection */
    quantityControl: FormControl;

    /** Number of future months to generate in the calendar */
    monthsToGenerate = 1;

    /** Current selected quantity */
    get selectedQuantity(): number {
        return this.quantityControl?.value || 1;
    }

    // === COMPUTED PROPERTIES ===

    /**
     * Gets the product's available quantity/stock
     * Used for calculating per-slot availability
     *
     * @returns Number of items available for this product
     */
    get productQuantity(): number {
        const quantity = (this.product as any)?.quantity?.amount || 0;
        return Math.max(0, quantity);
    }

    /**
     * Checks if the product has any available quantity
     * @returns true if product has quantity > 0
     */
    get hasProductQuantity(): boolean {
        return this.productQuantity > 0;
    }

    /**
     * Gets the advance booking hours requirement
     */
    get advanceBookingHours(): number {
        return this.product?.booking?.advanceBookingHours || 24;
    }

    /**
     * Gets the hold time in minutes
     */
    get holdTimeMinutes(): number {
        return this.product?.booking?.timeSlotInBetweenHold * 60 || 15;
    }

    /**
     * Gets the maximum slots per booking
     */
    get maxSlotsPerBooking(): number {
        return this.product?.booking?.maxBookingsPerUser || 5;
    }

    /**
     * Gets the slot duration from product configuration
     */
    get slotDuration(): number {
        return this.product?.booking?.timeSlotDuration || 1; // Default 1 hour
    }

    /**
     * Gets the slot duration in minutes for calculations
     */
    get slotDurationMinutes(): number {
        return this.slotDuration * 60; // Convert hours to minutes
    }

    /**
     * Gets the price per time slot
     */
    get pricePerSlot(): number {
        return (
            this.product?.booking?.pricePerTimeSlot ||
            this.product?.taxedPrice ||
            this.product?.price ||
            0
        );
    }

    /**
     * Gets the time slot start time
     */
    get timeSlotStartTime(): string {
        return this.product?.booking?.timeSlotStartTime || '09:00';
    }

    /**
     * Gets the time slot end time
     */
    get timeSlotEndTime(): string {
        return this.product?.booking?.timeSlotEndTime || '17:00';
    }

    /**
     * Checks if approval is required for bookings
     */
    get requireApproval(): boolean {
        return this.product?.booking?.requireApproval || false;
    }

    /**
     * Checks if the product is set up for quotation instead of direct booking
     */
    get isQuotable(): boolean {
        return this.product?.isOnlyQuotation || false;
    }

    constructor(
        private fb: FormBuilder,
        private bookingService: BookingService,
        private toastr: ToastrService,
        private translate: TranslateService,
        private companiesService: CompaniesService,
        private quotationRequestService: QuotationRequestService,
        private authService: AuthenticationService,
        private router: Router,
        private cartService: CartService, // Add cart service
        private formDataHelper: FormDataHelper // Add form data helper
    ) {
        this.initializeForm();
    }

    ngOnInit(): void {
        this.monthsToGenerate = this.product?.booking?.monthsToGenerate || 3;

        // Update quantity control when product changes
        this.updateQuantityControl();

        this.initializeBookingConfig();
        this.generateCalendarMonths();

        // Subscribe to language changes
        this.translate.onLangChange
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.generateCalendarMonths(); // Regenerate with new language
                this.updateCalendarDays();
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Initializes the reactive form for booking data
     */
    private initializeForm(): void {
        // Initialize quantity control
        this.quantityControl = new FormControl(
            this.product?.quantity?.min || 1,
            [
                Validators.min(this.product?.quantity?.min || 1),
                Validators.max(this.product?.quantity?.max || 99),
                Validators.required
            ]
        );

        this.bookingForm = this.fb.group({
            selectedDate: ['', Validators.required],
            selectedSlots: [[], Validators.required],
            quantity: this.quantityControl, // Add quantity to the form
            specialRequests: [''],
            customerName: [''],
            customerEmail: ['', [Validators.email]]
        });

        this.quotationForm = this.fb.group({
            description: ['']
        });

        // Subscribe to quantity changes
        this.quantityControl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(quantity => {
                this.calculateTotalPrice();
                this.updateSlotsAvailability();
            });
    }

    /**
     * Updates the quantity control when product changes
     */
    private updateQuantityControl() {
        if (this.quantityControl && this.product?.quantity) {
            const min = this.product.quantity.min || 1;
            const max = this.product.quantity.max || 99;
            const step = this.product.quantity.step || 1;

            // Update validators
            this.quantityControl.setValidators([
                Validators.min(min),
                Validators.max(max),
                Validators.required
            ]);

            // Set initial value if current value is out of bounds
            const currentValue = this.quantityControl.value;
            if (currentValue < min || currentValue > max) {
                this.quantityControl.setValue(min);
            }

            this.quantityControl.updateValueAndValidity();
        }
    }

    /**
     * Initializes booking configuration from product data
     */
    private initializeBookingConfig(): void {
        if (!this.product?.booking) {
            return;
        }

        // Initialize time slot configuration
        this.initializeTimeSlotConfig();
    }

    /**
     * Initializes time slot configuration from product booking settings
     */
    private initializeTimeSlotConfig(): void {
        // Check if product has specific time slot configuration
        if (
            this.product.booking?.timeSlotStartTime &&
            this.product.booking?.timeSlotEndTime
        ) {
            this.slotConfig = this.generateSlotsFromProductConfig();
        } else {
            // Create default slots if none configured
            this.slotConfig = this.generateDefaultTimeSlots();
        }
    }

    /**
     * Generates time slots from product configuration
     */
    private generateSlotsFromProductConfig(): TimeSlotConfig[] {
        const slots: TimeSlotConfig[] = [];
        const startTime = moment(this.timeSlotStartTime, 'HH:mm');
        const endTime = moment(this.timeSlotEndTime, 'HH:mm');
        const durationMinutes = this.slotDurationMinutes; // Duration in minutes
        const price = this.pricePerSlot;

        let currentTime = startTime.clone();

        while (
            currentTime
                .clone()
                .add(durationMinutes, 'minutes')
                .isSameOrBefore(endTime)
        ) {
            const slotEndTime = currentTime
                .clone()
                .add(durationMinutes, 'minutes');

            slots.push({
                startTime: currentTime.format('HH:mm'),
                endTime: slotEndTime.format('HH:mm'),
                duration: this.slotDuration, // Store duration in hours
                price: price,
                isRecurring: true,
                daysOfWeek: [1, 2, 3, 4, 5, 6, 0] // All days by default
            });

            // Move to next slot (add duration + hold time, both in minutes)
            currentTime.add(durationMinutes + this.holdTimeMinutes, 'minutes');
        }

        return slots;
    }

    /**
     * Generates default time slots for products without specific configuration
     */
    private generateDefaultTimeSlots(): TimeSlotConfig[] {
        const defaultSlots: TimeSlotConfig[] = [];
        const startHour = 9; // 9 AM
        const endHour = 17; // 5 PM
        const slotDurationHours = 1; // 1 hour
        const basePrice = this.product?.price || 100;

        for (let hour = startHour; hour < endHour; hour++) {
            const startTime = `${hour.toString().padStart(2, '0')}:00`;
            const endTime = `${(hour + slotDurationHours)
                .toString()
                .padStart(2, '0')}:00`;

            defaultSlots.push({
                startTime,
                endTime,
                duration: slotDurationHours, // Duration in hours
                price: basePrice,
                isRecurring: true,
                daysOfWeek: [1, 2, 3, 4, 5] // Monday to Friday
            });
        }

        return defaultSlots;
    }

    /**
     * Generates calendar months for date selection (current + next 2 months)
     */
    private generateCalendarMonths(): void {
        this.calendarMonths = [];
        const today = moment().locale('en');

        for (let i = 0; i < this.monthsToGenerate; i++) {
            const month = today.clone().add(i, 'month');
            this.calendarMonths.push(this.createCalendarMonth(month));
        }
    }

    /**
     * Creates a calendar month with available dates
     */
    private createCalendarMonth(month: moment.Moment): CalendarMonth {
        const monthStart = month.clone().startOf('month');
        const monthEnd = month.clone().endOf('month');
        const calendarStart = monthStart.clone().startOf('week');
        const calendarEnd = monthEnd.clone().endOf('week');

        const days: CalendarDay[] = [];
        let current = calendarStart.clone();

        while (current.isSameOrBefore(calendarEnd)) {
            const dateString = current.format('YYYY-MM-DD');
            const isCurrentMonth = current.isSame(month, 'month');
            const isToday = current.isSame(moment(), 'day');

            // Check if date meets advance booking requirements
            const minBookingTime = moment().add(
                this.advanceBookingHours,
                'hours'
            );
            const isAvailable =
                current.isAfter(minBookingTime) && isCurrentMonth;

            // Check if date has available slots based on day of week
            const dayOfWeek = current.day();
            const hasSlots = this.slotConfig.some(
                slot => !slot.daysOfWeek || slot.daysOfWeek.includes(dayOfWeek)
            );

            days.push({
                date: dateString,
                day: current.date(),
                isCurrentMonth,
                isToday,
                isAvailable: isAvailable && hasSlots && this.hasProductQuantity,
                isSelected: dateString === this.selectedDate,
                isUnavailable:
                    !isAvailable || !hasSlots || !this.hasProductQuantity,
                hasSlots
            });

            current.add(1, 'day');
        }

        // Get translated month name
        const monthNames = [
            'january',
            'february',
            'march',
            'april',
            'may',
            'june',
            'july',
            'august',
            'september',
            'october',
            'november',
            'december'
        ];
        const monthKey = monthNames[month.month()];
        const translatedMonthName = this.translate.instant(monthKey);

        return {
            year: month.year(),
            month: month.month(),
            monthName: `${translatedMonthName} ${month.year()}`, // Use translated month name
            days
        };
    }

    /**
     * Gets the current month being displayed
     */
    getCurrentMonth(): CalendarMonth {
        return (
            this.calendarMonths[this.currentMonthIndex] ||
            this.calendarMonths[0]
        );
    }

    /**
     * Navigates to previous month
     */
    previousMonth(): void {
        if (this.currentMonthIndex > 0) {
            this.currentMonthIndex--;
        }
    }

    /**
     * Navigates to next month
     */
    nextMonth(): void {
        if (this.currentMonthIndex < this.calendarMonths.length - 1) {
            this.currentMonthIndex++;
        }
    }

    /**
     * Handles date selection
     */
    onDateSelected(day: CalendarDay): void {
        if (!day.isAvailable || !day.isCurrentMonth) {
            return;
        }

        // Clear previous selection
        if (this.selectedDate) {
            this.clearSelectedSlots();
        }

        // Set new selected date
        this.selectedDate = day.date;
        this.bookingForm.patchValue({ selectedDate: day.date });

        // Update calendar display
        this.updateCalendarDays();

        // Load available slots for selected date
        this.loadAvailableSlots();
    }

    /**
     * Updates calendar days selection state
     */
    private updateCalendarDays(): void {
        this.calendarMonths.forEach(month => {
            month.days.forEach(day => {
                day.isSelected = day.date === this.selectedDate;
            });
        });
    }

    /**
     * Loads available time slots for the selected date
     */
    private loadAvailableSlots(): void {
        if (!this.selectedDate) {
            return;
        }

        this.loading = true;

        // Get product identifier for API call
        const productSlug = this.getProductIdentifier();

        // Include quantity in the API request
        this.bookingService
            .getAvailableTimeSlots(
                productSlug,
                this.selectedDate,
                this.selectedQuantity
            )
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (response.code === 200) {
                        this.processAvailableSlots(response);

                        // Update booking configuration if provided
                        if (response.bookingConfig) {
                            this.updateBookingConfig(response.bookingConfig);
                        }
                    } else {
                        this.toastr.error(this.translate.instant(response.msg));
                        this.availableSlots = [];
                    }
                    this.loading = false;
                },
                error: error => {
                    console.error('Error loading time slots:', error);
                    this.toastr.error(
                        this.translate.instant('failed_to_load_time_slots')
                    );
                    this.generateLocalTimeSlots();
                    this.loading = false;
                }
            });
    }

    /**
     * Updates booking configuration from API response
     */
    private updateBookingConfig(config: any): void {
        // Store the server-provided configuration
        // This could override local product configuration if needed
        // You can update component properties if the server config should override
        // local product configuration
        // Example:
        // this.maxSlotsPerBooking = config.maxSlotsPerBooking || this.maxSlotsPerBooking;
    }

    /**
     * Processes available slots from API response with quantity awareness
     */
    private processAvailableSlots(response: any): void {
        if (response.availableSlots && response.availableSlots.length > 0) {
            // Use slots from API - these are already filtered based on quantity requirements
            this.availableSlots = response.availableSlots.map(slot => ({
                id: slot.id,
                startTime: slot.startTime,
                endTime: slot.endTime,
                duration: slot.duration,
                price: slot.price,
                totalQuantity: slot.totalQuantity,
                bookedQuantity: slot.bookedQuantity,
                availableQuantity: slot.availableQuantity,
                isAvailable: slot.availableQuantity >= this.selectedQuantity,
                isSelected: false
            }));
        } else {
            // No slots available from API
            this.availableSlots = [];
        }
    }

    /**
     * Generates time slots locally based on product configuration with quantity checks
     */
    private generateLocalTimeSlots(): void {
        this.availableSlots = [];

        if (!this.selectedDate) {
            return;
        }

        const selectedMoment = moment(this.selectedDate);
        const dayOfWeek = selectedMoment.day();
        const now = moment();

        this.slotConfig.forEach(config => {
            // Check if slot is available for this day of week
            if (config.daysOfWeek && !config.daysOfWeek.includes(dayOfWeek)) {
                return;
            }

            // Create slot for the selected date
            const slotDateTime = selectedMoment.clone();
            const [startHour, startMinute] = config.startTime
                .split(':')
                .map(Number);
            slotDateTime
                .hour(startHour)
                .minute(startMinute)
                .second(0);

            // Check if slot meets advance booking requirement
            const advanceTime = now
                .clone()
                .add(this.advanceBookingHours, 'hours');
            if (slotDateTime.isBefore(advanceTime)) {
                return; // Slot is too soon
            }

            const slotId = `${this.selectedDate}_${config.startTime}_${config.endTime}`;
            const availableQuantity = this.productQuantity; // In local mode, assume full quantity available
            const canAccommodate = availableQuantity >= this.selectedQuantity;

            this.availableSlots.push({
                id: slotId,
                startTime: config.startTime,
                endTime: config.endTime,
                duration: config.duration,
                price: config.price,
                totalQuantity: this.productQuantity,
                bookedQuantity: 0, // Would come from API in real scenario
                availableQuantity: availableQuantity,
                isAvailable: canAccommodate,
                isSelected: false
            });
        });

        // Sort slots by start time
        this.availableSlots.sort((a, b) =>
            moment(a.startTime, 'HH:mm').diff(moment(b.startTime, 'HH:mm'))
        );
    }

    /**
     * Handles time slot selection with quantity validation
     */
    onSlotSelected(slot: AvailableTimeSlot): void {
        if (!slot.isAvailable) {
            this.toastr.warning(this.translate.instant('slot_not_available'));
            return;
        }

        // Check if slot can accommodate the selected quantity
        if (!this.canSlotAccommodateQuantity(slot)) {
            this.toastr.warning(
                this.translate.instant('insufficient_quantity_for_slot', {
                    slot: `${this.formatTime(
                        slot.startTime
                    )} - ${this.formatTime(slot.endTime)}`,
                    available: slot.availableQuantity,
                    required: this.selectedQuantity
                })
            );
            return;
        }

        // Check if we've reached maximum slots limit
        if (this.selectedSlots.length >= this.maxSlotsPerBooking) {
            this.toastr.warning(
                this.translate.instant('max_slots_reached', {
                    max: this.maxSlotsPerBooking
                })
            );
            return;
        }

        // Check if slot is already selected
        const existingSlotIndex = this.selectedSlots.findIndex(
            s => s.slotId === slot.id
        );

        if (existingSlotIndex >= 0) {
            // Remove slot if already selected (toggle functionality)
            this.selectedSlots.splice(existingSlotIndex, 1);
            slot.isSelected = false;
        } else {
            // Add new selected slot with quantity
            this.selectedSlots.push({
                slotId: slot.id,
                startTime: slot.startTime,
                endTime: slot.endTime,
                price: slot.price,
                quantity: this.selectedQuantity // Include quantity in selected slot
            });
            slot.isSelected = true;
        }

        // Update form and calculations
        this.bookingForm.patchValue({ selectedSlots: this.selectedSlots });
        this.calculateTotalPrice();
        this.emitSelectedSlotsChange();
    }

    /**
     * Removes a selected time slot
     */
    removeSelectedSlot(slotId: string): void {
        const index = this.selectedSlots.findIndex(s => s.slotId === slotId);
        if (index >= 0) {
            this.selectedSlots.splice(index, 1);

            // Update slot selection state
            const slot = this.availableSlots.find(s => s.id === slotId);
            if (slot) {
                slot.isSelected = false;
            }

            this.bookingForm.patchValue({ selectedSlots: this.selectedSlots });
            this.calculateTotalPrice();
            this.emitSelectedSlotsChange();
        }
    }

    /**
     * Clears all selected slots
     */
    clearSelectedSlots(): void {
        this.selectedSlots = [];
        this.availableSlots.forEach(slot => {
            slot.isSelected = false;
        });
        this.bookingForm.patchValue({ selectedSlots: [] });
        this.calculateTotalPrice();
        this.emitSelectedSlotsChange();
    }

    /**
     * Emits the current selection state with enhanced data
     */
    private emitSelectedSlotsChange(): void {
        const emitData: TimeSlotSelectionData = {
            slots: this.selectedSlots,
            selectedDate: this.selectedDate,
            totalSlots: this.totalSlots,
            totalPrice: this.totalPrice
        };

        this.selectedSlotsChanged.emit(emitData);
    }

    /**
     * Calculates total price for all selected slots with quantity
     */
    private calculateTotalPrice(): void {
        this.totalPrice = this.selectedSlots.reduce((total, slot) => {
            return total + slot.price * this.selectedQuantity;
        }, 0);

        this.totalSlots = this.selectedSlots.length;

        // Emit the price change
        this.priceCalculated.emit(this.totalPrice);
    }

    /**
     * Creates a time slot booking or quotation request or adds to cart
     */
    createBooking(): void {
        if (!this.selectedDate || this.selectedSlots.length === 0) {
            this.toastr.error(this.translate.instant('select_date_and_slots'));
            return;
        }

        if (!this.bookingForm.valid) {
            this.toastr.error(
                this.translate.instant('please_fill_required_fields')
            );
            return;
        }

        // Check if user is authenticated for quotation requests
        if (this.isQuotable && !this.authService.currentUserValue) {
            this.router.navigate(['/login'], {
                queryParams: { returnUrl: this.router.url }
            });
            return;
        }

        if (this.isQuotable) {
            this.showQuotationForm = true;
        } else {
            // For non-quotation products, add to cart instead of direct booking
            this.loading = true;
            this.addBookingToCart();
        }
    }

    /**
     * Adds the time slot booking item to cart
     */
    private addBookingToCart(): void {
        try {
            // Manual FormData creation to ensure proper structure
            const formData = new FormData();

            // Add base properties
            formData.append('productId', this.product._id);
            formData.append('amount', this.selectedQuantity.toString());

            // Add booking properties directly as individual fields
            formData.append('bookingType', 'timeSlots');
            formData.append('startDate', this.selectedDate);
            formData.append('endDate', this.selectedDate); // Same day for time slots
            formData.append('totalDays', '1'); // Time slots are always single day
            formData.append(
                'bookingQuantity',
                this.selectedQuantity.toString()
            );
            formData.append('totalPrice', this.totalPrice.toString());

            // Add time slots as individual entries
            this.selectedSlots.forEach((slot, index) => {
                formData.append(`timeSlots[${index}][slotId]`, slot.slotId);
                formData.append(
                    `timeSlots[${index}][startTime]`,
                    slot.startTime
                );
                formData.append(`timeSlots[${index}][endTime]`, slot.endTime);
                formData.append(
                    `timeSlots[${index}][price]`,
                    slot.price.toString()
                );
                formData.append(
                    `timeSlots[${index}][quantity]`,
                    this.selectedQuantity.toString()
                );
            });

            // Also add as JSON string for backward compatibility
            const bookingData = {
                bookingType: 'timeSlots',
                selectedDate: this.selectedDate,
                timeSlots: this.selectedSlots.map(slot => ({
                    slotId: slot.slotId,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    price: slot.price,
                    quantity: this.selectedQuantity
                })),
                totalSlots: this.totalSlots,
                quantity: this.selectedQuantity,
                totalPrice: this.totalPrice
            };
            formData.append('bookingData', JSON.stringify(bookingData));

            this.cartService.addItem(formData, this.product).subscribe({
                next: (response: any) => {
                    if (response.cart) {
                        // Log the specific item that was added
                        if (response.cart.items) {
                            const addedItem =
                                response.cart.items[
                                    response.cart.items.length - 1
                                ];
                        }
                    }

                    this.loading = false;

                    if (response.msg && response.code === 400) {
                        this.toastr.error(response.msg);
                        return;
                    }

                    // Handle cart warnings
                    if (response.cart?.warnings?.length > 0) {
                        response.cart.warnings.forEach(warning => {
                            this.toastr.warning(
                                warning.description[this.translate.currentLang],
                                warning.title[this.translate.currentLang]
                            );
                        });
                    }

                    // Handle cart errors
                    if (response.cart?.errors?.length > 0) {
                        response.cart.errors.forEach(error => {
                            this.toastr.error(
                                error.description[this.translate.currentLang],
                                error.title[this.translate.currentLang]
                            );
                        });
                        return;
                    }

                    // Success
                    this.toastr.success(
                        this.translate.instant(
                            'time_slot_booking_added_to_cart_success',
                            {
                                productName:
                                    this.product.name[
                                        this.translate.currentLang
                                    ] || this.product.name,
                                date: this.getFormattedSelectedDate(),
                                totalSlots: this.totalSlots,
                                quantity: this.selectedQuantity
                            }
                        )
                    );

                    this.addedToCart.emit({
                        cartItem: {
                            productId: this.product._id,
                            amount: this.selectedQuantity,
                            bookingType: 'timeSlots',
                            startDate: this.selectedDate,
                            endDate: this.selectedDate,
                            totalDays: 1,
                            bookingQuantity: this.selectedQuantity,
                            totalPrice: this.totalPrice,
                            timeSlots: this.selectedSlots.map(slot => ({
                                slotId: slot.slotId,
                                startTime: slot.startTime,
                                endTime: slot.endTime,
                                price: slot.price,
                                quantity: this.selectedQuantity
                            })),
                            bookingData: bookingData
                        },
                        response
                    });

                    this.resetForm();
                },
                error: (error: any) => {
                    this.loading = false;
                    console.error(
                        'Failed to add time slot booking to cart:',
                        error
                    );
                    this.toastr.error(
                        this.translate.instant('failed_to_add_booking_to_cart')
                    );
                }
            });
        } catch (error) {
            this.loading = false;
            console.error('Error preparing time slot booking for cart:', error);
            this.toastr.error(
                this.translate.instant('failed_to_prepare_booking')
            );
        }
    }

    /**
     * Closes the quotation form modal
     */
    closeQuotationForm(): void {
        this.showQuotationForm = false;
        this.quotationForm.reset();
    }

    /**
     * Submits the quotation request with user inputs
     */
    submitQuotationRequest(): void {
        this.loading = true;
        this.createQuotationRequest();
    }

    /**
     * Creates an actual booking (non-quotation)
     */
    private createActualBooking(): void {
        // Get company name first, then create booking
        this.companiesService.getCompanyName(this.product.companyId).subscribe({
            next: companyName => {
                console.log('Company Name:', companyName);

                // Now create booking with the resolved company name and quantity
                const bookingData = {
                    productId: String(
                        (this.product as any)._id || this.product?.id
                    ),
                    companyId: String(
                        this.product?.companyId ||
                            (this.product as any)?.companyData?._id
                    ),
                    bookingType: 'timeSlots' as const,
                    selectedDate: this.selectedDate,
                    timeSlots: this.selectedSlots.map(slot => ({
                        startTime: slot.startTime,
                        endTime: slot.endTime,
                        price: slot.price,
                        quantity: this.selectedQuantity, // Use selected quantity
                        duration: this.slotDuration
                    })),
                    totalSlots: this.totalSlots,
                    totalPrice: this.totalPrice,
                    quantity: this.selectedQuantity, // Include overall quantity
                    specialRequests:
                        this.bookingForm.get('specialRequests')?.value || '',
                    customerName:
                        this.bookingForm.get('customerName')?.value || '',
                    customerEmail:
                        this.bookingForm.get('customerEmail')?.value || '',
                    productName:
                        typeof this.product.name === 'object' &&
                        this.product.name !== null
                            ? {
                                  en:
                                      (this.product.name as {
                                          en?: string;
                                          ar?: string;
                                      }).en || '',
                                  ar:
                                      (this.product.name as {
                                          en?: string;
                                          ar?: string;
                                      }).ar || ''
                              }
                            : {
                                  en: String(this.product.name || ''),
                                  ar: String(this.product.name || '')
                              },
                    companyName: {
                        en: companyName.en || '',
                        ar: companyName.ar || ''
                    }
                };

                console.log(
                    'Time slot booking data with quantity:',
                    bookingData
                );

                this.bookingService.createBooking(bookingData).subscribe({
                    next: (response: any) => {
                        this.loading = false;
                        if (response && response.code === 200) {
                            this.toastr.success(
                                this.translate.instant(
                                    'booking_created_successfully'
                                )
                            );
                            this.bookingCreated.emit(response.booking);
                            this.resetForm();
                        } else {
                            this.toastr.error(
                                response?.msg ||
                                    this.translate.instant(
                                        'failed_to_create_booking'
                                    )
                            );
                        }
                    },
                    error: (error: any) => {
                        this.loading = false;
                        this.toastr.error(
                            this.translate.instant('booking_network_error')
                        );
                    }
                });
            },
            error: error => {
                console.error('Failed to get company name:', error);
                this.loading = false;
                this.toastr.error(
                    this.translate.instant('failed_to_get_company_info')
                );
            }
        });
    }

    /**
     * Creates a quotation request for time slot booking with quantity
     */
    private createQuotationRequest(): void {
        const description = this.quotationForm.get('description')?.value || '';

        const quotationData = {
            product: {
                _id: (this.product as any)._id || this.product?.id,
                name:
                    typeof this.product.name === 'object' &&
                    this.product.name !== null
                        ? {
                              en:
                                  (this.product.name as {
                                      en?: string;
                                      ar?: string;
                                  }).en || '',
                              ar:
                                  (this.product.name as {
                                      en?: string;
                                      ar?: string;
                                  }).ar || ''
                          }
                        : {
                              en: String(this.product.name || ''),
                              ar: String(this.product.name || '')
                          },
                qty: this.selectedQuantity, // Include quantity in quotation
                bookingDetails: {
                    type: 'timeSlots',
                    selectedDate: this.selectedDate,
                    quantity: this.selectedQuantity, // Include quantity in booking details
                    timeSlots: this.selectedSlots.map(slot => ({
                        startTime: slot.startTime,
                        endTime: slot.endTime,
                        price: slot.price,
                        quantity: this.selectedQuantity,
                        duration: this.slotDuration
                    })),
                    totalSlots: this.totalSlots
                }
            },
            companyId:
                this.product?.companyId ||
                (this.product as any)?.companyData?._id,
            description: description
        };

        this.quotationRequestService.singleRequest(quotationData).subscribe({
            next: (response: any) => {
                this.loading = false;
                this.showQuotationForm = false;
                if (response && response.code !== 400) {
                    if (response.addedNotification) {
                        this.authService.updateNotifications({
                            added: response.addedNotification
                        });
                    }
                    this.toastr.success(
                        this.translate.instant(
                            'quotation_request_sent_successfully'
                        )
                    );
                    this.quotationRequested.emit(response);
                    this.resetForm();
                } else {
                    this.toastr.error(
                        response?.msg ||
                            this.translate.instant(
                                'failed_to_send_quotation_request'
                            )
                    );
                }
            },
            error: (error: any) => {
                this.loading = false;
                this.showQuotationForm = false;
                this.toastr.error(
                    this.translate.instant('quotation_request_network_error')
                );
            }
        });
    }

    /**
     * Resets the form and selections
     */
    private resetForm(): void {
        this.bookingForm.reset();
        this.selectedDate = '';
        this.selectedSlots = [];
        this.availableSlots = [];
        this.totalPrice = 0;
        this.totalSlots = 0;
        this.updateCalendarDays();
    }

    /**
     * Gets product identifier for API calls
     */
    private getProductIdentifier(): string {
        return (
            (this.product as any)?.seo?.slug ||
            (this.product as any)?.slug ||
            this.product?.sku ||
            String(this.product?.id || (this.product as any)?._id)
        );
    }

    /**
     * Formats time for display with translated AM/PM
     */
    formatTime(time: string): string {
        const momentTime = moment(time, 'HH:mm');
        const hour = momentTime.format('h:mm');
        const period = momentTime.format('A').toLowerCase(); // 'am' or 'pm'
        const translatedPeriod = this.translate.instant(period);
        return `${hour} ${translatedPeriod}`;
    }

    /**
     * Gets translated weekday names
     */
    getTranslatedWeekDays(): string[] {
        return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map(day =>
            this.translate.instant(day)
        );
    }

    /**
     * Sets up moment.js locale based on current language
     */
    private setupMomentLocale(): void {
        const currentLang = this.translate.currentLang || 'en';
        moment.locale(currentLang);
    }

    /**
     * Gets formatted date for display with proper locale
     */
    getFormattedSelectedDate(): string {
        if (!this.selectedDate) {
            return '';
        }

        this.setupMomentLocale();

        // Use translated day and month names
        const date = moment(this.selectedDate);
        const dayName = this.translate.instant(this.getWeekdayKey(date.day()));
        const monthName = this.translate.instant(
            this.getMonthKey(date.month())
        );

        return `${dayName}, ${monthName} ${date.date()}, ${date.year()}`;
    }

    /**
     * Gets the translation key for weekday
     */
    private getWeekdayKey(dayIndex: number): string {
        const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        return days[dayIndex];
    }

    /**
     * Gets the translation key for month
     */
    private getMonthKey(monthIndex: number): string {
        const months = [
            'january',
            'february',
            'march',
            'april',
            'may',
            'june',
            'july',
            'august',
            'september',
            'october',
            'november',
            'december'
        ];
        return months[monthIndex];
    }

    /**
     * Checks if a slot has quantity available
     */
    hasSlotQuantityAvailable(slot: AvailableTimeSlot): boolean {
        return slot.availableQuantity > 0;
    }

    /**
     * Checks if we can add more slots
     */
    canAddMoreSlots(): boolean {
        return this.selectedSlots.length < this.maxSlotsPerBooking;
    }

    /**
     * Gets constraints text for display
     */
    getConstraintsText(): string {
        if (!this.product?.booking) return '';

        const constraints = [];

        if (this.advanceBookingHours > 0) {
            constraints.push(
                this.translate.instant('advance_booking_hours', {
                    hours: this.advanceBookingHours
                })
            );
        }

        if (this.maxSlotsPerBooking > 0) {
            constraints.push(
                this.translate.instant('max_slots_per_booking', {
                    max: this.maxSlotsPerBooking
                })
            );
        }

        if (this.holdTimeMinutes > 0) {
            constraints.push(
                this.translate.instant('hold_time_minutes', {
                    minutes: this.holdTimeMinutes
                })
            );
        }

        if (this.requireApproval) {
            constraints.push(this.translate.instant('requires_approval'));
        }

        return constraints.join(' • ');
    }

    /**
     * TrackBy function for ngFor performance
     */
    trackBySlotId(index: number, slot: SelectedTimeSlot): string {
        return slot.slotId;
    }

    /**
     * Gets duration display text for a slot
     */
    getDurationDisplayText(durationHours: number): string {
        if (durationHours === 1) {
            return this.translate.instant('duration_hour_singular');
        } else if (durationHours < 1) {
            const minutes = Math.round(durationHours * 60);
            return this.translate.instant('duration_minutes', { minutes });
        } else {
            return this.translate.instant('duration_hours', {
                hours: durationHours
            });
        }
    }

    /**
     * Checks if a slot can accommodate the selected quantity
     */
    canSlotAccommodateQuantity(slot: AvailableTimeSlot): boolean {
        return slot.availableQuantity >= this.selectedQuantity;
    }

    /**
     * Updates the availability of slots based on the selected quantity.
     */
    private updateSlotsAvailability(): void {
        this.availableSlots.forEach(slot => {
            slot.isAvailable = slot.availableQuantity >= this.selectedQuantity;
        });
    }

    /**
     * Buy Now: add selected time slots to cart and immediately checkout only that booking.
     * Uses singleCheckout=true so backend creates an isolated cart for immediate checkout.
     */
    public buyNow() {
        if (this.loading) return;
        if (!this.selectedDate || this.selectedSlots.length === 0) {
            this.toastr.error(this.translate.instant('select_date_and_slots'));
            return;
        }
        if (this.isQuotable && !this.authService.currentUserValue) {
            this.router.navigate(['/login'], {
                queryParams: { returnUrl: this.router.url }
            });
            return;
        }
        if (this.isQuotable) {
            this.toastr.error(this.translate.instant('cannot_buy_quotation'));
            return;
        }

        this.loading = true;

        try {
            const formData = new FormData();
            formData.append('productId', String(this.product._id));
            formData.append('amount', String(this.selectedQuantity));
            formData.append('bookingType', 'timeSlots');
            formData.append('startDate', this.selectedDate);
            formData.append('endDate', this.selectedDate);
            formData.append('totalDays', '1');
            formData.append('bookingQuantity', String(this.selectedQuantity));
            // ensure single scalar value for totals
            formData.set('totalPrice', String(this.totalPrice));

            // add timeSlots entries
            this.selectedSlots.forEach((slot, index) => {
                formData.append(`timeSlots[${index}][slotId]`, slot.slotId);
                formData.append(
                    `timeSlots[${index}][startTime]`,
                    slot.startTime
                );
                formData.append(`timeSlots[${index}][endTime]`, slot.endTime);
                formData.append(
                    `timeSlots[${index}][price]`,
                    String(slot.price)
                );
                formData.append(
                    `timeSlots[${index}][quantity]`,
                    String(slot.quantity || this.selectedQuantity)
                );
            });

            const bookingData = {
                bookingType: 'timeSlots',
                selectedDate: this.selectedDate,
                timeSlots: this.selectedSlots.map(s => ({
                    slotId: s.slotId,
                    startTime: s.startTime,
                    endTime: s.endTime,
                    price: s.price,
                    quantity: s.quantity || this.selectedQuantity
                })),
                totalSlots: this.totalSlots,
                quantity: this.selectedQuantity,
                totalPrice: this.totalPrice
            };
            formData.set('bookingData', JSON.stringify(bookingData));
            formData.set('singleCheckout', 'true');

            this.cartService.addItem(formData, this.product).subscribe({
                next: (response: any) => {
                    this.loading = false;

                    if (response?.code === 400 && response?.msg) {
                        this.toastr.error(response.msg);
                        return;
                    }

                    if (response?.cart?.warnings?.length > 0) {
                        response.cart.warnings.forEach(w =>
                            this.toastr.warning(
                                w.description?.[this.translate.currentLang] ||
                                    w.description
                            )
                        );
                    }

                    if (response?.cart?.errors?.length > 0) {
                        response.cart.errors.forEach(e =>
                            this.toastr.error(
                                e.description?.[this.translate.currentLang] ||
                                    e.description
                            )
                        );
                        return;
                    }

                    const returnedCart = response.cart;
                    if (!returnedCart || !returnedCart._id) {
                        this.toastr.error(
                            this.translate.instant('failed_to_add_to_cart')
                        );
                        return;
                    }

                    // Robust matching: match by productId + bookingType + selectedDate + exact timeSlots (slotId + quantity) + totalPrice
                    const normalizedSlots = (this.selectedSlots || []).map(
                        s => ({
                            slotId: String(s.slotId),
                            quantity: Number(
                                s.quantity || this.selectedQuantity
                            )
                        })
                    );

                    const matchItem = _.find(
                        returnedCart.items || [],
                        (it: any) => {
                            try {
                                if (
                                    String(it.productId) !==
                                    String(this.product._id)
                                )
                                    return false;
                                if (it.bookingType !== 'timeSlots')
                                    return false;
                                if (
                                    (it.startDate && String(it.startDate)) !==
                                    String(this.selectedDate)
                                )
                                    return false;

                                const itemSlotsRaw =
                                    it.timeSlots ||
                                    (it.bookingData &&
                                        it.bookingData.timeSlots) ||
                                    [];
                                const itemSlots = (itemSlotsRaw || []).map(
                                    (s: any) => ({
                                        slotId: String(
                                            s.slotId ?? s.id ?? s.slotId
                                        ),
                                        quantity: Number(
                                            s.quantity ??
                                                s.qty ??
                                                s.quantity ??
                                                1
                                        )
                                    })
                                );

                                // lengths must match and every selected slot must exist with same quantity
                                if (itemSlots.length !== normalizedSlots.length)
                                    return false;
                                const slotsMatch = normalizedSlots.every(ns =>
                                    itemSlots.some(
                                        is =>
                                            is.slotId === ns.slotId &&
                                            Number(is.quantity) ===
                                                Number(ns.quantity)
                                    )
                                );
                                if (!slotsMatch) return false;

                                const itemTotal = Number(
                                    it.totalPrice ??
                                        it.total ??
                                        it.unitTotal ??
                                        0
                                );
                                if (
                                    Math.abs(
                                        itemTotal - Number(this.totalPrice || 0)
                                    ) > 0.0001
                                )
                                    return false;

                                return true;
                            } catch (e) {
                                return false;
                            }
                        }
                    );

                    // fallback: last matching productId & timeSlots bookingType
                    const fallback = _.findLast(
                        returnedCart.items || [],
                        (it: any) =>
                            String(it.productId) === String(this.product._id) &&
                            it.bookingType === 'timeSlots'
                    );

                    const targetItem = matchItem || fallback;
                    if (!targetItem) {
                        this.toastr.success(
                            this.translate.instant('added_to_cart')
                        );
                        const params = {
                            cartId: returnedCart._id,
                            itemIds: []
                        };
                        this.cartService.userCart = {
                            cartId: returnedCart._id,
                            items: []
                        };
                        this.cartService.saveCartId(params).subscribe(() =>
                            this.router.navigate(['/checkout'], {
                                state: { data: params }
                            })
                        );
                        return;
                    }

                    // Checkout only the found item
                    this.checkoutSingleItem(
                        returnedCart._id,
                        targetItem.itemId
                    );
                },
                error: (err: any) => {
                    this.loading = false;
                    console.error('BuyNow addBookingToCart error', err);
                    this.toastr.error(
                        this.translate.instant('failed_to_add_booking_to_cart')
                    );
                }
            });
        } catch (err) {
            this.loading = false;
            console.error('BuyNow prepare error', err);
            this.toastr.error(
                this.translate.instant('failed_to_prepare_booking')
            );
        }
    }

    /**
     * Checkout a single item from a cart (sets userCart and navigates to checkout)
     */
    private checkoutSingleItem(cartId: string, itemId: string) {
        const items = [itemId];
        const params = { cartId, itemIds: items };
        this.cartService.userCart = { cartId, items };
        this.cartService.saveCartId(params).subscribe({
            next: () =>
                this.router.navigate(['/checkout'], {
                    state: { data: params }
                }),
            error: () =>
                this.router.navigate(['/checkout'], { state: { data: params } })
        });
    }
}
